import { useLayoutEffect, useRef, useState } from 'react';
import { createUpload, orgID, type UploadStage } from '@yingbaba/api';
import { Button, Notice, compactFileSize } from '@yingbaba/ui';
import { Upload, CheckCircle2 } from '@yingbaba/icons';
import { useUnsavedChanges } from './UnsavedChanges';

type Kind = 'original' | 'preview' | 'rights_proof';
export const digitalFileKinds = [
  {
    kind: 'original' as const,
    label: '原始交付',
    limit: 200,
    accept: '',
    hint: '买家下载的完整文件，可用 ZIP 打包工程与依赖',
  },
  {
    kind: 'preview' as const,
    label: '公开预览',
    limit: 30,
    accept: '.jpg,.jpeg,.png,.mp4,.mp3,.wav',
    hint: '带水印图片、MP4 或试听音频；建议同时上传封面图',
  },
  {
    kind: 'rights_proof' as const,
    label: '权属证明',
    limit: 10,
    accept: '.jpg,.jpeg,.png,.pdf',
    hint: '原创证明或完整授权文件，仅审核人员与本组织可查看',
  },
];
type Job = {
  id: string;
  name: string;
  kind: Kind;
  size: number;
  percent: number;
  state: 'waiting' | UploadStage | 'done' | 'failed';
  error?: string;
  attempt?: ReturnType<typeof createUpload>;
};
const labels = {
  waiting: '等待上传',
  preparing: '准备上传',
  uploading: '正在上传',
  confirming: '校验并保存中',
  done: '已保存',
  failed: '未完成',
};
export function DigitalUploads({
  revisionID,
  fileCount,
  disabled,
  onUploaded,
  onPending,
}: {
  revisionID: string;
  fileCount: number;
  disabled: boolean;
  onUploaded: () => Promise<unknown>;
  onPending: (value: boolean) => void;
}) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [error, setError] = useState('');
  const latest = useRef<Job[]>([]);
  const running = useRef(false);
  const generation = useRef(0);
  const alive = useRef(true);
  const callbacks = useRef({ onUploaded, onPending });
  callbacks.current = { onUploaded, onPending };
  const organization = orgID();
  function update(next: Job[]) {
    latest.current = next;
    if (alive.current) {
      setJobs(next);
      callbacks.current.onPending(next.some((job) => job.state !== 'done'));
    }
  }
  function clear() {
    generation.current++;
    latest.current.forEach((job) => job.attempt?.cancel());
    running.current = false;
    update([]);
  }
  useLayoutEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      clear();
      callbacks.current.onPending(false);
    };
  }, [revisionID, organization]);
  useUnsavedChanges({
    dirty: jobs.some((job) => job.state !== 'done'),
    title: '仍有资源文件未上传完成',
    description: '已保存的文件会保留。离开后，未完成的文件需要重新选择。',
    discardLabel: '离开并清除队列',
    continueLabel: '继续上传',
    discard: clear,
  });
  async function process() {
    if (running.current || disabled) return;
    running.current = true;
    const current = generation.current;
    try {
      for (;;) {
        const job = latest.current.find((item) => item.state === 'waiting');
        if (!job || current !== generation.current || !alive.current) break;
        const patch = (changes: Partial<Job>) => {
          if (alive.current && generation.current === current)
            update(
              latest.current.map((item) => (item.id === job.id ? { ...item, ...changes } : item)),
            );
        };
        try {
          await job.attempt!.run(
            (stage) => patch({ state: stage }),
            (percent) => patch({ percent }),
          );
          // Refresh revision numbers before making submit/edit actions available.
          await callbacks.current.onUploaded();
          patch({ state: 'done', attempt: undefined, percent: 100, error: undefined });
        } catch (err) {
          patch({
            state: 'failed',
            error: err instanceof Error ? err.message : '上传未完成，请重试',
          });
        }
      }
    } finally {
      if (current === generation.current) running.current = false;
    }
  }
  function add(kind: Kind, files: File[]) {
    if (disabled || !files.length) return;
    if (fileCount + latest.current.filter((j) => j.state !== 'done').length + files.length > 30) {
      setError('每个版本最多 30 个文件，请减少选择数量或先移除不需要的文件');
      return;
    }
    const spec = digitalFileKinds.find((item) => item.kind === kind)!;
    const invalid = files.find(
      (file) =>
        file.size < 1 ||
        file.size > spec.limit * 1048576 ||
        (spec.accept &&
          !spec.accept.split(',').includes('.' + file.name.split('.').pop()?.toLowerCase())),
    );
    if (invalid) {
      setError(`${invalid.name}：请选择非空、格式符合要求且不超过 ${spec.limit} MB 的文件`);
      return;
    }
    setError('');
    const next = files.map((file): Job => {
      const form = new FormData();
      form.append('kind', kind);
      form.append('file', file);
      return {
        id: crypto.randomUUID(),
        name: file.name,
        kind,
        size: file.size,
        percent: 0,
        state: 'waiting',
        attempt: createUpload(`/digital/revisions/${revisionID}/files`, form),
      };
    });
    update([...latest.current.filter((job) => job.state !== 'done'), ...next]);
    void process();
  }
  function remove(job: Job) {
    job.attempt?.cancel();
    update(latest.current.filter((item) => item.id !== job.id));
  }
  return (
    <section className="digital-upload-area" aria-label="上传数字资源文件">
      <div className="digital-upload-slots">
        {digitalFileKinds.map((spec) => (
          <label
            key={spec.kind}
            className={`digital-upload-slot ${disabled ? 'is-disabled' : ''}`}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              add(spec.kind, Array.from(event.dataTransfer.files));
            }}
          >
            <Upload size={20} aria-hidden="true" />
            <strong>{spec.label}</strong>
            <span className="small muted">{spec.hint}</span>
            <span className="digital-upload-prompt">选择或拖入文件 · 每个最多 {spec.limit} MB</span>
            <input
              type="file"
              multiple
              accept={spec.accept || undefined}
              className="sr-only"
              disabled={disabled}
              aria-label={`上传${spec.label}文件`}
              onChange={(event) => {
                add(spec.kind, Array.from(event.target.files || []));
                event.target.value = '';
              }}
            />
          </label>
        ))}
      </div>
      <p className="small muted">
        每个版本最多 30
        个文件。支持多选，文件依次上传；预览请使用独立的水印或试听版本，勿上传原件作为公开预览。
      </p>
      {error && <Notice>{error}</Notice>}
      {!!jobs.length && (
        <div className="digital-upload-queue" aria-label="资源上传队列">
          <div className="row spread wrap">
            <strong className="small" role="status">
              已保存 {jobs.filter((job) => job.state === 'done').length} / {jobs.length} 个文件
            </strong>
            {jobs.some((job) => job.state === 'done') && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => update(latest.current.filter((job) => job.state !== 'done'))}
              >
                清除已完成
              </Button>
            )}
          </div>
          <ul>
            {jobs.map((job) => (
              <li key={job.id} className="digital-upload-job">
                <div className="digital-upload-job-info">
                  <strong>{job.name}</strong>
                  <span className="small muted">
                    {digitalFileKinds.find((spec) => spec.kind === job.kind)?.label} ·{' '}
                    {compactFileSize(job.size)} · {labels[job.state]}
                    {job.state === 'uploading' ? ` ${job.percent}%` : ''}
                  </span>
                  {job.state === 'uploading' && (
                    <progress value={job.percent} max={100} aria-label={`${job.name}上传进度`} />
                  )}
                  {job.state === 'confirming' && (
                    <span className="small muted">文件已传输，正在核对完整性，无需重复上传。</span>
                  )}
                  {job.error && (
                    <span className="small form-error" role="alert">
                      {job.error}
                    </span>
                  )}
                </div>
                {job.state === 'done' ? (
                  <CheckCircle2 size={20} aria-label="已保存" />
                ) : (
                  <div className="row wrap">
                    {job.state === 'failed' && (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={disabled}
                        onClick={() => {
                          update(
                            latest.current.map((item) =>
                              item.id === job.id
                                ? { ...item, state: 'waiting', error: undefined }
                                : item,
                            ),
                          );
                          void process();
                        }}
                      >
                        重试
                      </Button>
                    )}
                    {/* After bytes arrive, completion can already be committing. Don't imply cancellation rolls it back. */}
                    {job.state !== 'confirming' && (
                      <Button size="sm" variant="ghost" onClick={() => remove(job)}>
                        {job.state === 'waiting' || job.state === 'failed' ? '移除' : '取消'}
                      </Button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
